import React from "react";
import styles from "./Select.module.css";
import { getClassName } from "../../utils/className.js";
import SelectTooltip from "./SelectTooltip.jsx";

/**
 * @typedef {Object} SelectOption
 * @property {string | number} value - The unique value of the option.
 * @property {string} label - The display text of the option.
 * @property {boolean} [disabled] - Whether the option is disabled.
 */

/**
 * MultiSelectTrigger renders the trigger element for custom multi-select dropdowns.
 * It supports standard display button triggers, searchable input triggers, and chip/tag displays.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.searchable - If true, enables search filtering.
 * @param {boolean} props.isOpen - Whether the dropdown menu is open.
 * @param {string} props.searchValue - The current search query.
 * @param {Array<SelectOption>} props.selectedOptions - List of currently selected option objects.
 * @param {string} props.placeholder - Custom placeholder text.
 * @param {boolean} props.disabled - Whether the field is disabled.
 * @param {string} props.triggerClassName - Calculated CSS classes for the trigger container.
 * @param {React.RefObject<HTMLInputElement | HTMLButtonElement | HTMLDivElement>} props.triggerRef - Reference to the trigger container or element.
 * @param {React.RefObject<HTMLInputElement>} props.inputRef - Reference specifically to the input field in chip-mode.
 * @param {boolean} props.isChipMode - If true, displays selected values as interactive chips.
 * @param {string} props.selectionDisplay - Comma-separated labels of selected options.
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} props.handleInputChange - Event handler for search text changes.
 * @param {(e: React.MouseEvent) => void} props.handleInputClick - Event handler for trigger clicks.
 * @param {() => void} props.handleInputFocus - Event handler for input focus.
 * @param {(e: React.KeyboardEvent) => void} props.handleKeyDown - Keyboard events handler for accessibility.
 * @param {() => void} props.handleToggle - Toggles dropdown open/close state.
 * @param {(e: React.MouseEvent) => void} props.handleArrowClick - Click handler for the arrow icon.
 * @param {(e: React.MouseEvent) => void} props.handleClearSearch - Click handler for the clear icon/button.
 * @param {(value: string | number) => void} props.handleSelect - Selection/removal callback for clicking chip close buttons.
 * @param {React.ComponentType | React.ReactElement} [props.arrowIcon] - Custom icon component or element to replace the default dropdown arrow.
 * @param {string} [props.tooltip] - Tooltip text shown (with a short hover delay) when hovering over the trigger.
 * @param {boolean} [props.tooltipLight] - If true, (white background, dark text).
 */
const MultiSelectTrigger = ({
  searchable,
  isOpen,
  searchValue,
  selectedOptions,
  placeholder,
  disabled,
  triggerClassName,
  triggerRef,
  inputRef,
  isChipMode,
  selectionDisplay,
  handleInputChange,
  handleInputClick,
  handleInputFocus,
  handleKeyDown,
  handleToggle,
  handleArrowClick,
  handleClearSearch,
  handleSelect,
  arrowIcon,
  tooltip,
  tooltipLight,
}) => {
  const renderChips = () => (
    <>
      {selectedOptions.map((opt) => (
        <span key={opt.value} className={getClassName(styles.chip, "select-chip")}>
          <span className={getClassName(styles.chipLabel, "select-chip-label")}>{opt.label}</span>
          <button
            type="button"
            className={getClassName(styles.chipRemove, "select-chip-remove")}
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(opt.value);
            }}
            disabled={disabled}
            aria-label={`Remove ${opt.label}`}
          >
            &times;
          </button>
        </span>
      ))}
    </>
  );

  const renderSearchableTrigger = () => {
    if (isChipMode) {
      return (
        <div
          ref={triggerRef}
          className={triggerClassName}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={disabled}
        >
          {renderChips()}
          <input
            ref={inputRef}
            type="text"
            className={getClassName(styles.chipInput, "select-chip-input", {
              [styles.chipInputHidden]: !isOpen && selectedOptions.length > 0,
              "select-chip-input-hidden": !isOpen && selectedOptions.length > 0,
            })}
            disabled={disabled}
            value={searchValue}
            placeholder={selectedOptions.length > 0 ? "" : placeholder}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
          />
        </div>
      );
    }

    return (
      <input
        ref={triggerRef}
        type="text"
        className={triggerClassName}
        disabled={disabled}
        value={isOpen ? searchValue : selectionDisplay}
        placeholder={isOpen && selectionDisplay ? selectionDisplay : placeholder}
        onChange={handleInputChange}
        onClick={handleInputClick}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-disabled={disabled}
      />
    );
  };

  const renderNormalTrigger = () => {
    return (
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-disabled={disabled}
      >
        {isChipMode && selectedOptions.length > 0 ? (
          renderChips()
        ) : (
          <span
            className={getClassName(styles.selectionText, "select-selection-text", {
              [styles.placeholder]: selectedOptions.length === 0,
              "select-placeholder": selectedOptions.length === 0,
            })}
          >
            {selectedOptions.length > 0 ? selectionDisplay : placeholder}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {searchable ? renderSearchableTrigger() : renderNormalTrigger()}

      {(() => {
        if (!arrowIcon) {
          return (
            <svg
              className={getClassName(styles.arrowIcon, "select-arrow-icon", {
                [styles.arrowOpen]: isOpen,
                "select-arrow-open": isOpen,
              })}
              viewBox="0 0 12 7"
              aria-hidden="true"
              onClick={handleArrowClick}
            >
              <path d="M1 0 L6 5 L11 0 L12 1 L6 7 L0 1 Z" />
            </svg>
          );
        }

        const isComponent =
          typeof arrowIcon === "function" ||
          (typeof arrowIcon === "object" &&
            arrowIcon &&
            arrowIcon.$$typeof &&
            !React.isValidElement(arrowIcon));

        const element = isComponent ? React.createElement(arrowIcon) : arrowIcon;

        if (React.isValidElement(element)) {
          return React.cloneElement(element, {
            className: getClassName(
              styles.arrowIcon,
              "select-arrow-icon",
              {
                [styles.arrowOpen]: isOpen,
                "select-arrow-open": isOpen,
              },
              element.props.className
            ),
            onClick: (e) => {
              element.props.onClick?.(e);
              handleArrowClick(e);
            },
          });
        }

        return (
          <div
            className={getClassName(styles.arrowIcon, "select-arrow-icon", {
              [styles.arrowOpen]: isOpen,
              "select-arrow-open": isOpen,
            })}
            onClick={handleArrowClick}
          >
            {element}
          </div>
        );
      })()}

      {searchable && isOpen && searchValue && (
        <button
          type="button"
          className={getClassName(styles.clearSearchBtn, "select-clear-search-btn")}
          onClick={handleClearSearch}
          title="Clear search"
        >
          &times;
        </button>
      )}

      <SelectTooltip text={tooltip} light={tooltipLight} anchorRef={triggerRef} />
    </>
  );
};

export default MultiSelectTrigger;
